// Fleet CP3 — pure maintenance helpers (due-state, interval formatting,
// schedule roll-forward). No React/supabase deps so they stay unit-testable.

export interface MaintenanceSchedule {
  id: string
  vehicle_id: string | null
  equipment_id: string | null
  title: string
  interval_type: 'date' | 'mileage'
  interval_days: number | null
  interval_miles: number | null
  next_due_date: string | null
  next_due_miles: number | null
  last_completed_at: string | null
  active: boolean
  notes: string | null
}

export type DueState = 'ok' | 'soon' | 'overdue'

// Thresholds: overdue when past due; "soon" within 14 days OR within 500 miles.
const SOON_DAYS = 14
const SOON_MILES = 500

export function scheduleDueState(s: MaintenanceSchedule, vehicleOdometer: number | null, nowMs: number): DueState {
  if (!s.active) return 'ok'
  if (s.interval_type === 'date') {
    if (!s.next_due_date) return 'ok'
    const dueMs = new Date(s.next_due_date).getTime()
    if (dueMs < nowMs) return 'overdue'
    if (dueMs < nowMs + SOON_DAYS * 86400000) return 'soon'
    return 'ok'
  }
  // mileage
  if (s.next_due_miles == null || vehicleOdometer == null) return 'ok'
  if (vehicleOdometer >= s.next_due_miles) return 'overdue'
  if (s.next_due_miles - vehicleOdometer <= SOON_MILES) return 'soon'
  return 'ok'
}

const RANK: Record<DueState, number> = { ok: 0, soon: 1, overdue: 2 }
export function worstState(states: DueState[]): DueState {
  return states.reduce<DueState>((acc, s) => (RANK[s] > RANK[acc] ? s : acc), 'ok')
}

export function formatInterval(s: Pick<MaintenanceSchedule, 'interval_type' | 'interval_days' | 'interval_miles'>): string {
  if (s.interval_type === 'mileage') return s.interval_miles ? `Every ${s.interval_miles.toLocaleString()} mi` : 'Mileage-based'
  return s.interval_days ? `Every ${s.interval_days} days` : 'Date-based'
}

// Roll a schedule forward after a service is logged against it. Date-based:
// next_due_date = performedAt + interval_days. Mileage-based: next_due_miles =
// odometerAtService + interval_miles (falls back to prior threshold + interval
// if no odometer was captured). Returns only the fields to patch.
export function rollForward(
  s: MaintenanceSchedule,
  performedAt: string,
  odometerAtService: number | null,
): { last_completed_at: string; next_due_date?: string | null; next_due_miles?: number | null } {
  const patch: { last_completed_at: string; next_due_date?: string | null; next_due_miles?: number | null } = { last_completed_at: performedAt }
  if (s.interval_type === 'date' && s.interval_days) {
    const d = new Date(performedAt + 'T00:00:00Z')
    d.setUTCDate(d.getUTCDate() + s.interval_days)
    patch.next_due_date = d.toISOString().slice(0, 10)
  } else if (s.interval_type === 'mileage' && s.interval_miles) {
    const base = odometerAtService != null ? odometerAtService : (s.next_due_miles ?? 0)
    patch.next_due_miles = base + s.interval_miles
  }
  return patch
}
